import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/auth/AuthContext";
import { useCart } from "@/cart/CartProvider";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminPreOrders from "@/pages/AdminPreOrders";
import AdminStripeEvents from "@/pages/AdminStripeEvents";
import AdminMenu from "@/pages/AdminMenu";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, RefreshCw, Mail, Phone, MapPin, DollarSign } from "lucide-react";

const STATUSES = [
    { value: "pending", label: "Pending payment" },
    { value: "confirmed", label: "Confirmed" },
    { value: "preparing", label: "Preparing" },
    { value: "out_for_delivery", label: "Out for delivery" },
    { value: "delivered", label: "Delivered" },
    { value: "canceled", label: "Canceled" },
];

const STATUS_COLOR = {
    pending: "bg-mint text-forest",
    confirmed: "bg-forest text-white",
    preparing: "bg-gold text-navy",
    out_for_delivery: "bg-sage text-white",
    delivered: "bg-forest text-white",
    canceled: "bg-burnt text-white",
};

export default function AdminPage() {
    const { user, loading } = useAuth();
    const { openAuth } = useCart();
    const navigate = useNavigate();
    const [orders, setOrders] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState("all");
    const [tab, setTab] = useState("orders");

    const authHeaders = () => {
        const token = localStorage.getItem("session_token");
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const res = await axios.get(`${API}/admin/orders`, {
                headers: authHeaders(),
            });
            setOrders(res.data.orders || []);
        } catch (err) {
            if (err?.response?.status === 403) {
                toast.error("Admin access only.");
                navigate("/", { replace: true });
            } else {
                toast.error("Couldn't load orders");
                setOrders([]);
            }
        } finally {
            setRefreshing(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            // Don't silently redirect — open the sign-in modal so the user
            // knows they need to authenticate to view this page.
            openAuth();
            return;
        }
        if (!user.is_admin) {
            toast.error("This account is not an admin.");
            navigate("/", { replace: true });
            return;
        }
        load();
    }, [loading, user, load, navigate, openAuth]);

    const updateStatus = async (order_id, status) => {
        try {
            await axios.patch(
                `${API}/admin/orders/${order_id}`,
                { status },
                { headers: authHeaders() }
            );
            setOrders((prev) =>
                prev.map((o) =>
                    o.order_id === order_id ? { ...o, status } : o
                )
            );
            toast.success("Status updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update");
        }
    };

    const markPaid = async (order_id, reason) => {
        try {
            const res = await axios.post(
                `${API}/admin/orders/${order_id}/mark-paid`,
                { reason, send_confirmation_email: true },
                { headers: authHeaders() },
            );
            const updated = res.data?.order;
            if (updated) {
                setOrders((prev) =>
                    prev.map((o) => (o.order_id === order_id ? updated : o)),
                );
            }
            if (res.data?.already_paid) {
                toast.info("Already marked paid");
            } else if (res.data?.email_dispatched) {
                toast.success(
                    "Order marked paid · Confirmation email sent to customer",
                );
            } else {
                toast.success("Order marked paid (email send queued)");
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't mark as paid",
            );
        }
    };

    const filtered = useMemo(() => {
        if (!orders) return null;
        if (filter === "all") return orders;
        if (filter === "active")
            return orders.filter((o) =>
                ["confirmed", "preparing", "out_for_delivery"].includes(o.status)
            );
        return orders.filter((o) => o.status === filter);
    }, [orders, filter]);

    const stats = useMemo(() => {
        if (!orders) return { count: 0, revenue: 0, paid: 0 };
        const paid = orders.filter((o) => o.payment_status === "paid");
        return {
            count: orders.length,
            paid: paid.length,
            revenue: paid.reduce((s, o) => s + (o.total || 0), 0),
        };
    }, [orders]);

    if (loading || !user || !user.is_admin) {
        return (
            <div className="min-h-screen bg-cream text-navy font-sans flex flex-col">
                <div className="relative hero-gradient text-white pb-20 pt-32 px-6">
                    <HeaderBar onSignIn={openAuth} dark />
                    <div className="max-w-3xl mx-auto text-center">
                        <p className="text-[11px] tracking-[0.4em] text-gold font-bold uppercase">
                            Admin
                        </p>
                        <h1 className="mt-3 font-serif font-black text-4xl sm:text-5xl">
                            {loading
                                ? "Loading…"
                                : !user
                                ? "Please sign in"
                                : "Admin access required"}
                        </h1>
                        <p className="mt-4 text-white/80 max-w-md mx-auto">
                            {loading
                                ? "Checking your session…"
                                : !user
                                ? "Sign in with the admin account to manage orders."
                                : "Your account is not an admin. Sign in with the admin email instead."}
                        </p>
                        {!loading && !user && (
                            <button
                                data-testid="admin-signin-cta"
                                onClick={openAuth}
                                className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold transition-all active:scale-95 shadow-[0_10px_28px_rgba(244,164,53,0.45)]"
                            >
                                Sign in
                            </button>
                        )}
                        {!loading && user && !user.is_admin && (
                            <button
                                onClick={() => navigate("/")}
                                className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold"
                            >
                                Back to homepage
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex-1" />
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream pb-20">
            <div className="relative hero-gradient grain pb-24 pt-2">
                <HeaderBar dark onSignIn={() => {}} />
                <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-36 sm:pt-32 flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <p className="text-mint/80 tracking-[0.3em] text-xs font-bold uppercase">
                            Caribbean Meal Prep · Admin
                        </p>
                        <h1 className="mt-2 font-serif font-black text-white text-4xl sm:text-5xl">
                            Kitchen orders
                        </h1>
                    </div>
                    <Button
                        data-testid="admin-refresh-btn"
                        onClick={load}
                        disabled={refreshing}
                        className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold"
                    >
                        <RefreshCw
                            size={16}
                            className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-6 sm:px-8 mt-6 lg:-mt-16 relative z-10">
                {/* Tabs */}
                <div className="flex gap-2 mb-6 flex-wrap">
                    <TabBtn
                        active={tab === "orders"}
                        onClick={() => setTab("orders")}
                        testId="admin-tab-orders"
                    >
                        Orders
                    </TabBtn>
                    <TabBtn
                        active={tab === "menu"}
                        onClick={() => setTab("menu")}
                        testId="admin-tab-menu"
                    >
                        Menu
                    </TabBtn>
                    <TabBtn
                        active={tab === "preorders"}
                        onClick={() => setTab("preorders")}
                        testId="admin-tab-preorders"
                    >
                        Pre-orders
                    </TabBtn>
                    <TabBtn
                        active={tab === "marketing"}
                        onClick={() => setTab("marketing")}
                        testId="admin-tab-marketing"
                    >
                        Marketing
                    </TabBtn>
                    <TabBtn
                        active={tab === "stripe"}
                        onClick={() => setTab("stripe")}
                        testId="admin-tab-stripe"
                    >
                        Stripe
                    </TabBtn>
                </div>

                {tab === "marketing" ? (
                    <AdminMarketing />
                ) : tab === "menu" ? (
                    <AdminMenu />
                ) : tab === "preorders" ? (
                    <AdminPreOrders />
                ) : tab === "stripe" ? (
                    <AdminStripeEvents />
                ) : (
                <>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <StatCard
                        label="Total Orders"
                        value={stats.count}
                        accent="text-navy"
                    />
                    <StatCard
                        label="Paid Orders"
                        value={stats.paid}
                        accent="text-forest"
                    />
                    <StatCard
                        label="Revenue"
                        value={`$${stats.revenue.toFixed(2)}`}
                        accent="text-gold"
                        isPrice
                    />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-2 mb-5">
                    {[
                        { v: "all", l: "All" },
                        { v: "active", l: "Active" },
                        { v: "pending", l: "Pending" },
                        { v: "confirmed", l: "Confirmed" },
                        { v: "delivered", l: "Delivered" },
                        { v: "canceled", l: "Canceled" },
                    ].map((f) => (
                        <button
                            key={f.v}
                            data-testid={`filter-${f.v}`}
                            onClick={() => setFilter(f.v)}
                            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                                filter === f.v
                                    ? "bg-forest text-white"
                                    : "bg-white text-navy hover:bg-mint border border-mint"
                            }`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>

                {/* Orders */}
                {filtered === null ? (
                    <div className="flex items-center gap-2 text-muted2">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white rounded-[20px] p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                        <div className="text-5xl">📭</div>
                        <p className="mt-3 font-bold text-navy">
                            No orders match this filter.
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {filtered.map((o) => (
                            <AdminOrderCard
                                key={o.order_id}
                                order={o}
                                onStatusChange={(s) =>
                                    updateStatus(o.order_id, s)
                                }
                                onMarkPaid={markPaid}
                            />
                        ))}
                    </ul>
                )}
                </>
                )}

                <div className="mt-10 text-center">
                    <Link
                        to="/"
                        className="text-forest font-bold text-sm hover:underline"
                    >
                        ← Back to site
                    </Link>
                </div>
            </div>

            <div className="mt-20">
                <Footer />
            </div>
        </div>
    );
}

function StatCard({ label, value, accent, isPrice = false }) {
    return (
        <div className="bg-white rounded-[20px] p-5 shadow-[0_6px_24px_rgba(0,0,0,0.07)]">
            <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                {label}
            </p>
            <p
                className={`mt-1 ${isPrice ? "font-display tracking-wider" : "font-serif font-black"} text-3xl ${accent}`}
            >
                {value}
            </p>
        </div>
    );
}

function TabBtn({ active, onClick, children, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                active
                    ? "bg-forest text-white shadow-[0_6px_18px_rgba(15,81,50,0.35)]"
                    : "bg-white text-navy hover:bg-mint border border-mint"
            }`}
        >
            {children}
        </button>
    );
}

function AdminOrderCard({ order, onStatusChange, onMarkPaid }) {
    const [markingPaid, setMarkingPaid] = useState(false);
    const date = order.created_at
        ? new Date(order.created_at).toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
          })
        : "";
    return (
        <li
            data-testid={`admin-order-${order.order_id}`}
            className="bg-white rounded-[20px] p-5 shadow-[0_6px_24px_rgba(0,0,0,0.07)] grid lg:grid-cols-[1fr_280px] gap-4"
        >
            <div>
                <div className="flex items-center gap-3 flex-wrap">
                    <span
                        className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${STATUS_COLOR[order.status] || "bg-mint text-forest"}`}
                    >
                        {order.status?.replace(/_/g, " ")}
                    </span>
                    <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${order.payment_status === "paid" ? "bg-mint text-forest" : "bg-cream text-muted2 border border-mint"}`}
                    >
                        {order.payment_status}
                    </span>
                    <span className="text-xs text-muted2">{date}</span>
                </div>
                <p className="mt-2 font-bold text-navy">
                    {order.customer?.name || "—"}
                </p>
                <div className="mt-1 text-xs text-muted2 space-y-0.5">
                    <p className="flex items-center gap-1.5 break-all">
                        <Mail size={12} className="text-forest" />
                        {order.customer?.email}
                    </p>
                    <p className="flex items-center gap-1.5">
                        <Phone size={12} className="text-forest" />
                        {order.customer?.phone}
                    </p>
                    <p className="flex items-start gap-1.5">
                        <MapPin
                            size={12}
                            className="text-forest mt-0.5 flex-shrink-0"
                        />
                        {order.customer?.address}
                    </p>
                </div>
                <ul className="mt-3 text-sm text-navy space-y-0.5">
                    {order.items.map((it, i) => (
                        <li key={i}>
                            <span className="font-bold">{it.quantity}×</span>{" "}
                            {it.name}{" "}
                            <span className="text-muted2">
                                · ${it.line_total.toFixed(2)}
                            </span>
                        </li>
                    ))}
                </ul>
                {order.notes && (
                    <p className="mt-2 text-xs text-muted2 italic">
                        Note: {order.notes}
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-3 lg:items-end">
                <div className="text-right">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                        Total
                    </p>
                    <p className="font-display text-4xl text-forest tabular-nums">
                        ${order.total.toFixed(2)}
                    </p>
                </div>
                <p className="text-xs text-muted2">
                    Deliver:{" "}
                    <span className="font-bold text-navy">
                        {order.delivery_date}
                    </span>
                </p>
                <Select
                    value={order.status}
                    onValueChange={onStatusChange}
                >
                    <SelectTrigger
                        data-testid={`admin-status-${order.order_id}`}
                        className="w-full lg:w-[200px] rounded-full border-mint"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                                {s.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <a
                    href={`mailto:${order.customer?.email}?subject=Caribbean%20Meal%20Prep%20%E2%80%94%20Your%20order&body=Hi%20${encodeURIComponent(order.customer?.name || "")},`}
                    className="text-xs text-forest font-bold hover:underline"
                    data-testid={`admin-email-${order.order_id}`}
                >
                    Email customer →
                </a>
                {order.payment_status !== "paid" &&
                    order.payment_status !== "paid_in_full" && (
                        <button
                            type="button"
                            data-testid={`admin-mark-paid-${order.order_id}`}
                            disabled={markingPaid}
                            onClick={async () => {
                                const reason = window.prompt(
                                    `Mark order ${order.order_id} as PAID?\n\nThis flips payment_status to "paid", confirms the order, and emails the customer their confirmation.\n\nOptional note (e.g., "Stripe webhook missed — verified in dashboard"):`,
                                    "",
                                );
                                if (reason === null) return; // cancelled
                                setMarkingPaid(true);
                                try {
                                    await onMarkPaid(order.order_id, reason);
                                } finally {
                                    setMarkingPaid(false);
                                }
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold hover:bg-goldDark text-navy text-[11px] font-bold transition-all active:scale-95 disabled:opacity-50"
                        >
                            {markingPaid ? (
                                <Loader2 size={11} className="animate-spin" />
                            ) : (
                                <DollarSign size={11} />
                            )}
                            Mark as paid
                        </button>
                    )}
            </div>
        </li>
    );
}
